import { EditableSection } from '../helpers/EditableSection';
import { trpc } from '@/lib/trpc';
import { StatCard } from './StatCard';
import { BucketCard } from './BucketCard';
import { MetricBox } from './MetricBox';

interface QuizStatsProps {
  quizId: string;
}

export function QuizStats({ quizId }: QuizStatsProps) {
  const { data: stats, isLoading } = trpc.admin.quiz.getQuizStats.useQuery({ quizId });

  if (isLoading) {
    return (
      <EditableSection title="Stats" collapsible={true} defaultExpanded={false}>
        <div className="text-gray-500 text-sm">Loading statistics...</div>
      </EditableSection>
    );
  }

  if (!stats) {
    return (
      <EditableSection title="Stats" collapsible={true} defaultExpanded={false}>
        <div className="text-gray-500 text-sm">No statistics available</div>
      </EditableSection>
    );
  }

  return (
    <EditableSection title="Stats" collapsible={true} defaultExpanded={true}>
      <div className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Total Completed" value={stats.totalCompleted} bgColor="bg-primary-soft" textColor="text-brand-dark" />
          <StatCard label="Total Started" value={stats.totalStarted} bgColor="bg-gray-100" textColor="text-gray-700" />
          <StatCard label="Completion Rate" value={`${stats.completionRate}%`} bgColor="bg-primary-soft" textColor="text-brand-dark" />
          <StatCard label="Response Count" value={stats.totalCompleted} bgColor="bg-gray-100" textColor="text-gray-700" />
        </div>

        {/* Bucket Distribution */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Bucket Distribution</h3>
          <div className="grid grid-cols-2 gap-4">
            <BucketCard count={stats.bucketDistribution.one} label="Bucket One" total={stats.totalCompleted} color="text-brand" />
            <BucketCard count={stats.bucketDistribution.two} label="Bucket Two" total={stats.totalCompleted} color="text-brand-dark" />
          </div>
          {stats.bucketDistribution.none > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded text-center">
              <div className="text-sm text-gray-600">
                {stats.bucketDistribution.none} user{stats.bucketDistribution.none !== 1 ? 's' : ''} without bucket assignment
              </div>
            </div>
          )}
        </div>

        {/* Per-Question Stats */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Question Statistics</h3>
          <div className="space-y-4">
            {stats.questionStats.map((qStat) => (
              <div key={qStat.questionId} className="border-b border-gray-100 pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      Q{qStat.questionNumber}: {qStat.questionText}
                    </div>
                    <div className="text-sm text-gray-500">
                      {qStat.questionType} • {qStat.responseCount} responses
                    </div>
                  </div>
                </div>

                {/* Numeric/Percentage Stats */}
                {(qStat.questionType === 'numeric' || qStat.questionType === 'percentage') && qStat.stats.average !== undefined && (
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                    {[
                      { label: 'Average', value: qStat.stats.average },
                      { label: 'Median', value: qStat.stats.median },
                      { label: 'Min', value: qStat.stats.min },
                      { label: 'Max', value: qStat.stats.max },
                      { label: 'Std Dev', value: qStat.stats.standardDeviation },
                    ].map(({ label, value }) => (
                      <MetricBox
                        key={label}
                        label={label}
                        value={`${value}${qStat.questionType === 'percentage' && label !== 'Std Dev' ? '%' : ''}`}
                      />
                    ))}
                  </div>
                )}

                {/* Boolean Stats */}
                {qStat.questionType === 'boolean' && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-green-50 p-2 rounded">
                      <div className="text-xs text-green-700">True</div>
                      <div className="font-semibold text-green-900">
                        {qStat.stats.trueCount} ({qStat.stats.truePercentage}%)
                      </div>
                    </div>
                    <div className="bg-red-50 p-2 rounded">
                      <div className="text-xs text-red-700">False</div>
                      <div className="font-semibold text-red-900">
                        {qStat.stats.falseCount} ({qStat.stats.falsePercentage}%)
                      </div>
                    </div>
                  </div>
                )}

                {/* Single Choice Stats */}
                {qStat.questionType === 'singleChoice' && qStat.stats.distribution && (
                  <div className="mt-2 space-y-1">
                    {qStat.stats.distribution.map((item: any) => (
                      <div key={item.option} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                        <span className="font-medium">{item.option}</span>
                        <span className="text-gray-600">
                          {item.count} ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Perception Gap - Only for Numeric/Percentage questions */}
                {qStat.perceptionGap?.userAverage !== undefined && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-3">
                    <div className="text-sm font-semibold text-yellow-900 mb-2">
                      📊 Perception vs. Reality
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-yellow-800">Parents' Average:</span>
                        <span className="font-semibold text-yellow-900">
                          {qStat.perceptionGap.userAverage}{qStat.questionType === 'percentage' ? '%' : ''}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-800">Actual Average:</span>
                        <span className="font-semibold text-yellow-900">
                          {qStat.perceptionGap.actualAverage}{qStat.questionType === 'percentage' ? '%' : ''}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-yellow-300 pt-1 mt-1">
                        <span className="text-yellow-800 font-medium">Gap:</span>
                        <span className={`font-bold ${qStat.perceptionGap.gap > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {qStat.perceptionGap.gap > 0 ? '+' : ''}{qStat.perceptionGap.gap}
                          {qStat.questionType === 'percentage' ? '%' : ''} ({qStat.perceptionGap.gapPercentage > 0 ? '+' : ''}{qStat.perceptionGap.gapPercentage}%)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </EditableSection>
  );
}